import { Asset } from "../../../models/asset";
import { Scheduling } from "../../../models/schedule";
import { SignedRevision } from "../../../models/signedRevision";

interface Blockchain {
  transactionDate: string;
  transactionHash: string;
}

interface IPeriodicReviewsPdfProps {
  asset: Asset | null;
  blockchainData: (scheduleId: string) => SignedRevision | undefined;
  order: string;
  paginateSchedulings: (schedulings: Scheduling[]) => Scheduling[][];
}

const PeriodicReviewsPdf = ({
  asset,
  blockchainData,
  order,
  paginateSchedulings,
}: IPeriodicReviewsPdfProps) => {
  /**
   * Extrai o número do título (ex: "1º" -> 1, "37º" -> 37)
   */
  const extractNumber = (title: string): number => {
    const match = title.match(/\d+/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  /**
   * Ordena os agendamentos com os 3 principais sempre em primeiro
   * Os outros são ordenados pelo número do título (1º, 2º, ... 37º)
   * @param schedulings Array de Scheduling
   * @returns Array ordenado com os 3 principais em primeiro
   */
  const sortSchedulings = (schedulings: Scheduling[]): Scheduling[] => {
    const mainTypes = ["Entrega técnica", "Revisão de entrega", "Assentamento"];
    
    const main = schedulings.filter(s => mainTypes.includes(s.checkup?.name || ""));
    const others = schedulings.filter(s => !mainTypes.includes(s.checkup?.name || ""));
    
    const sortedMain = mainTypes
      .map(type => main.find(s => s.checkup?.name === type))
      .filter((item): item is Scheduling => item !== undefined);
    
    const sortedOthers = others.sort((a, b) => {
      const numA = extractNumber(a.checkup?.name || "");
      const numB = extractNumber(b.checkup?.name || "");
      
      return order === "down" ? numA - numB : numB - numA;
    });
    
    return [...sortedMain, ...sortedOthers];
  };

  return (
    <div
      className="revision-content px-20 max-w-[85%] mx-auto"
      style={{ top: "-10px" }}
      id="periodic-reviews-pdf"
    >
      {paginateSchedulings(sortSchedulings([
        ...(asset?.schedulings?.previous ?? []),
        ...(asset?.schedulings?.next ?? []),
      ])).map((schedulings, index) => (
        <div key={index} className={`h-[1040px] mt-[10px]`}>
          {index === 0 && (
            <>
              <h3 className="flex gap-6 margin-bottom-10 text-size-20">
                <span className="rioglyph rioglyph-book text-size-20"></span>
                Manutenções e Revisões
              </h3>
              <div className="flex justify-between items-center">
                <span className="text-size-14">Revisões por ordem</span>
                <img src="/assets/images/rio-icon.svg" alt="Rio icon" />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-6 scrollable mt-2">
            {schedulings.map((scheduleCheckup: Scheduling, index: number) => {
              const blockchain = blockchainData(scheduleCheckup.id);
              return (
                <Card
                  scheduling={scheduleCheckup}
                  isSigned={!!blockchain?.id}
                  finished={scheduleCheckup.isFinished}
                  blockchain={{
                    transactionDate:
                      (typeof blockchain?.reviewDate === "string"
                        ? new Date(blockchain?.reviewDate).toLocaleDateString(
                          "pt-BR"
                        )
                        : blockchain?.reviewDate?.toLocaleDateString(
                          "pt-BR"
                        )) ?? "",
                    transactionHash: blockchain?.id ?? "",
                  }}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

interface ICardProps {
  scheduling: Scheduling;
  isSigned?: boolean;
  finished?: boolean;
  blockchain: Blockchain;
}
const Card = ({ scheduling, isSigned, finished, blockchain }: ICardProps) => {
  return (
    <div className={`border border-width-2 rounded-lg revision-card`}>
      <div
        className={`flex justify-between items-center mb-2 p-2 px-4 border-color-light border-b`}
      >
        <p className="p-0 m-0 revision-card__title truncate text-size-12">
          {scheduling.checkup?.name}
        </p>
        <span
          className={`rioglyph rioglyph-eye-option ${scheduling.checkup?.services?.length
              ? "cursor-pointer text-color-primary"
              : "cursor-not-allowed"
            }`}
        />
      </div>

      <>
        <div
          className={`flex justify-between gap-3 px-4 border-color-light border-b`}
        >
          <div className="form-group w-full">
            <label>OS nº:</label>
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling?.os : ""}
              className="form-control mt-1 p-2 border border-gray-300 rounded-md text-gray-700 input-sm bg-white"
            />
          </div>
          <div className={`form-group w-full`}>
            <label>Data:</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.date?.toLocaleDateString("pt-BR")
                  : ""
              }
              className="form-control mt-1 p-2 border border-gray-300 rounded-md text-gray-700 input-sm bg-white"
            />
          </div>
        </div>

        <div
          className={`flex justify-between gap-3 px-4 border-color-light border-b`}
        >
          <div className="form-group w-full">
            <label>Horímetro:</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.hourmeter?.toLocaleString("pt-Br")
                  : ""
              }
              className="form-control mt-1 p-2 border border-gray-300 rounded-md text-gray-700 input-sm bg-white"
            />
          </div>

          <div className="form-group w-full">
            <label>Km:</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.odometer?.toLocaleString("pt-Br")
                  : ""
              }
              className="form-control mt-1 p-2 border border-gray-300 rounded-md text-gray-700 input-sm bg-white"
            />
          </div>
        </div>

        <div className="form-group flex flex-wrap pl-2 field-section border-color-light border-b">
          {[
            {
              label: "Assentamento",
              value: "Assentamento",
            },
            {
              label: "L",
              value: "L",
            },
            {
              label: "MP1",
              value: "MP1",
            },
            {
              label: "MP2",
              value: "MP2",
            },
            {
              label: "Intermediária",
              value: "Intermediária"
            },
          ].map((item) => (
            <div
              key={item.value}
              className="text-left mr-4 my-2 flex items-center"
            >
              <div
                className={`w-3 h-3 border border-gray-400 rounded-sm mr-2 flex items-center justify-center ${scheduling.checkup?.name === item.value
                    ? "bg-primary border-color-info"
                    : "bg-white"
                  }`}
                style={{
                  minWidth: "12px",
                  minHeight: "12px",
                }}
              >
                {scheduling.checkup?.name === item.value && (
                  <svg
                    width="8"
                    height="6"
                    viewBox="0 0 8 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 2.5L3 4.5L7 0.5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <span className="text-size-10 text-color-dark">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="form-group flex flex-wrap justify-between px-4 field-section">
          <label className="block w-full text-size-12 text-color-dark">
            Concessionária:
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.dealership?.name : ""}
              className="form-control mt-1 p-2 border border-gray-300 rounded-md text-gray-700 input-sm bg-white"
            />
          </label>
          <label className="block w-full text-size-12 text-color-dark">
            Consultor:
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.consultant : ""}
              className="form-control mt-1 p-2 border border-gray-300 rounded-md text-gray-700 input-sm bg-white"
            />
          </label>
        </div>

        <div className="flex items-center font-semibold text-color-primary text-teal-600 footer-card p-2 border-color-light border-t">
          {finished && !isSigned ? (
            <div
              className={`text-size-10 flex items-center gap-8 justify-between w-full pt-20`}
            >
              <span>Realizado</span>
              <img src="/assets/images/check-icon.svg" alt="Check icon" />
            </div>
          ) : isSigned ? (
            <div className="w-full">
              <div
                className={`text-size-10 flex items-center gap-8 justify-between w-full`}
              >
                <span>Autenticado via Blockchain</span>
                <img
                  src="/assets/images/blockchain-enabled.svg"
                  alt="Enabled Blockchain icon"
                />
              </div>
              <div
                className={`text-size-10 w-full rounded-md bg-[#F0FEFF] p-2 mt-2`}
              >
                <div className="flex items-center justify-between text-size-10">
                  <div>Transcation ID:</div>
                  <div>{blockchain.transactionDate}</div>
                </div>
                <div
                  className="text-size-10 break-all"
                  style={{ wordBreak: "break-all", maxWidth: 350 }}
                >
                  {blockchain.transactionHash}
                </div>
              </div>
            </div>
          ) : (
            <div
              className={`text-size-10 flex items-center gap-8 justify-between w-full text-color-gray pt-20`}
            >
              <span>Aguardando autenticação</span>
              <img
                src="/assets/images/blockchain-disabled.svg"
                alt="Disabled Blockchain icon"
              />
            </div>
          )}
        </div>
      </>
    </div>
  );
};

export default PeriodicReviewsPdf;
